import { casOpsApi, CollectionFilteredModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';
import { FeatureCollection, Geometry } from 'geojson';
import { FiltersState } from '../slices/filtersSlice';
import { getUrlParams } from './helpers';

// This is considered legacy.
export enum FenceType {
  Standard = 'Standard',
  Intersection = 'Intersection',
  WetRoad = 'WetRoad',
}

export enum GeofenceTypeV2 {
  Intersection = 'Intersection',
  WetRoad = 'WetRoad',
  Slope = 'Slope',
}

export enum GeofenceState {
  Normal = 'Normal',
  RequireAttention = 'RequireAttention',
}

export interface GeofenceProperties {
  id: number;
  radius?: number;
  name: string;
  fenceType: FenceType;
  slope?: boolean;
  areas?: string; // Comma-separated list of Geofence Area IDs.
  ags?: string; // Comma-separated list of Asset Group IDs.
  geofenceType?: string; // Geofence shape
  sty?: number; // Geofence Style ID
  ra?: 1; // Will only be present for new geofences that have the 'RequireAttention' state.
  // Pin Drop geofences may have the following optional properties:
  pdrpasset?: string; // The name of the pin drop asset.
  pdrptime?: string; // The creation time of the pindrop geofence.
}

export const isGeofenceProperties = (obj: unknown): obj is GeofenceProperties => {
  return (
    obj instanceof Object &&
    'id' in obj &&
    typeof obj.id === 'number' &&
    'name' in obj &&
    typeof obj.name === 'string' &&
    'fenceType' in obj &&
    typeof obj.fenceType === 'string'
  );
};

interface GeofenceBasicModel extends ModelWithId {
  name: string; // Max 128 chars with regex "^[a-zA-Z0-9-_\\.\\s]+$"
  desc: string; // description. Max 255 chars
  fts: GeofenceTypeV2[];
  stid: number; // See geofenceStyles
  ags: number[]; // See assetGroups
  vert: number; // Number of vertices in the geofence. GEN3 devices support a vert upper limit.
}

export interface GeofenceVersionModel {
  version?: number;
  time?: string; // ISO timestamp
}

export interface GeofencesListModel extends GeofenceBasicModel {
  state: GeofenceState; // Used to indicate if a geofence needs attention.
}

interface GeofencesListResponseModel
  extends CollectionFilteredModel<GeofencesListModel>,
    GeofenceVersionModel {}

export interface GeofenceFilters
  extends Partial<
    Pick<
      FiltersState,
      'assetGroups' | 'geofenceTypes' | 'geofenceAreas' | 'geofenceStyles' | 'geofenceState'
    >
  > {}

export type GeofenceQueryParams = GeofenceFilters;

export interface GeofenceQueryModel {
  query: GeofenceQueryParams;
}

export interface GeofenceShapeModel {
  points: number[][];
  shape: 'Polygon' | 'Circle';
  radius: number | null;
}

export interface GeofenceRulesModel {
  areaIds: number[]; // See geofenceAreas
  createEventOnEntry: boolean;
  createEventOnExit: boolean;
  alarmLogicMuteAll: boolean;
  alarmLogicRunAreaLogic: boolean;
  createEventIfAtAndDidNotStop: boolean;
  createEventIfAtAndSpeeding: number | null; // See geofenceSpeedLimits
  speedLimitLoaded: number | null; // See geofenceSpeedLimits
  createEventIfAtForLongerThanSeconds: number | null; // See geofenceHoldTimes (isGreater = false)
  createEventIfAtForLessThanSeconds: number | null; // See geofenceHoldTimes (isGreater = true)
  driveOutputs: number[];
  createEventOnInputs: number[];
  interventionEnabled: boolean | null;
}

export interface GeofenceModel extends GeofenceBasicModel {
  wetDuration: number | null; // Duration for wet roads, in minutes (range 1 to 500)
  followMeName: string | null; // Follow me geofence name (technically, this should be the name of a valid asset but this is a legacy capability that GEN3 does not support... so it's a just a text field)
  shape: GeofenceShapeModel;
  rules: GeofenceRulesModel;
}

export interface CreateGeofenceModel extends Omit<GeofenceModel, 'id' | 'vert'> {}

export interface EditGeofenceModel extends Omit<GeofenceModel, 'id' | 'vert'> {}

export interface GeofenceFormModel extends CreateGeofenceModel, EditGeofenceModel {}

export const geofencesApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listGeofences: build.query<GeofencesListResponseModel, GeofenceQueryModel | void>({
      query: arg => ({
        url: 'api/geofences',
        params: arg ? getUrlParams(arg.query) : undefined,
      }),
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.Geofence, id: TagList }] : [],
    }),
    getGeofenceVersion: build.query<GeofenceVersionModel, void>({
      query: () => 'api/geofencesettings/version',
      providesTags: (result, error, arg) => (result ? [QueryTag.GeofenceVersion] : []),
    }),
    getGeofencesGeoJson: build.query<
      FeatureCollection<Geometry, GeofenceProperties>,
      GeofenceQueryModel | void
    >({
      query: arg => ({
        url: 'api/geofences',
        params: arg ? getUrlParams(arg.query) : undefined,
        headers: {
          Accept: 'application/vnd.geo+json',
        },
      }),
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.GeofenceGeoJson, id: TagList }] : [],
    }),
    getGeofence: build.query<GeofenceModel, ModelWithId>({
      query: ({ id }) => `api/geofences/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.Geofence, id: id }] : [],
    }),
    createGeofence: build.mutation<void, CreateGeofenceModel>({
      query: body => ({
        url: 'api/geofences',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: QueryTag.Geofence, id: TagList },
        { type: QueryTag.GeofenceGeoJson, id: TagList },
        QueryTag.GeofenceVersion,
        { type: QueryTag.GeofenceHoldTime, id: TagList },
        { type: QueryTag.GeofenceSpeedLimit, id: TagList },
        { type: QueryTag.GeofenceStyle, id: TagList },
        { type: QueryTag.GeofenceArea, id: TagList },
      ],
    }),
    editGeofence: build.mutation<void, EditGeofenceModel & ModelWithId>({
      query: ({ id, ...body }) => ({
        url: `api/geofences/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.Geofence, id: TagList },
              { type: QueryTag.Geofence, id: id },
              { type: QueryTag.GeofenceGeoJson, id: TagList },
              QueryTag.GeofenceVersion,
              { type: QueryTag.GeofenceHoldTime, id: TagList },
              { type: QueryTag.GeofenceSpeedLimit, id: TagList },
              { type: QueryTag.GeofenceStyle, id: TagList },
              { type: QueryTag.GeofenceArea, id: TagList },
            ]
          : [],
    }),
    deleteGeofence: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/geofences/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.Geofence, id: TagList },
              { type: QueryTag.Geofence, id: id },
              { type: QueryTag.GeofenceGeoJson, id: TagList },
              QueryTag.GeofenceVersion,
              { type: QueryTag.GeofenceHoldTime, id: TagList },
              { type: QueryTag.GeofenceSpeedLimit, id: TagList },
              { type: QueryTag.GeofenceStyle, id: TagList },
              { type: QueryTag.GeofenceArea, id: TagList },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListGeofencesQuery,
  useGetGeofenceVersionQuery,
  useGetGeofencesGeoJsonQuery,
  useGetGeofenceQuery,
  useCreateGeofenceMutation,
  useEditGeofenceMutation,
  useDeleteGeofenceMutation,
} = geofencesApi;
